import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addContentLock,
  removeContentLock,
  useContentLockState,
  type ContentLockState,
} from '../content-lock';

/**
 * O modo edição de uma tela sobre o lock de um conteúdo. `start` pede o lock
 * (o lápis), `finish` solta (concluir, cancelar), e desmontar a tela também
 * solta — inclusive a navegação do SPA.
 *
 * `contentId` `undefined` desliga o hook: nada de lock, nada de estado.
 */
export function useContentEditLock(contentId: string | undefined) {
  const state = useContentLockState(contentId);
  const [acquiring, setAcquiring] = useState(false);
  // O `acquiring` do estado só chega no próximo render; o clique duplo no
  // mesmo tick dispararia um segundo `POST`.
  const inFlight = useRef(false);

  /**
   * Devolve o estado em que o pedido terminou, ou `undefined` quando o clique
   * foi ignorado (outro pedido no ar). Quem tem mais de uma área de edição
   * sobre o mesmo lock decide pela resposta qual delas abre: o `editing` só
   * muda no próximo render.
   */
  const start = useCallback(async (): Promise<ContentLockState | undefined> => {
    if (contentId === undefined || inFlight.current) return undefined;
    inFlight.current = true;
    setAcquiring(true);
    try {
      return await addContentLock(contentId);
    } finally {
      inFlight.current = false;
      setAcquiring(false);
    }
  }, [contentId]);

  const finish = useCallback(() => {
    if (contentId !== undefined) removeContentLock(contentId);
  }, [contentId]);

  useEffect(() => {
    if (contentId === undefined) return;
    return () => removeContentLock(contentId);
  }, [contentId]);

  return {
    state,
    acquiring,
    /** Com o lock: a tela edita. */
    editing: state?.status === 'held',
    /** Perdeu o lock no meio: o form fica, mas não salva. */
    lost: state?.status === 'lost',
    start,
    finish,
  };
}
